import { Vector3, Vector2, Plane } from 'three';

import Volume3D from './Volume3DUtil';

const tempVec = new Vector3();

export default class SliceViewUtil {
  constructor(sliceView) {
    Object.entries(sliceView).forEach(([key, value]) => {
      this[key] = value;
    });

    this.orient = new Vector3(this.planeNormal.X, this.planeNormal.Y, this.planeNormal.Z);

    this.volume3D = new Volume3D(sliceView.volume3D);

    this.isAxialView = this.orientation.equals(new Vector3(0, 0, 1));
    this.isCoronalView = this.orientation.equals(new Vector3(0, 1, 0));
    this.isSagittalView = this.orientation.equals(new Vector3(1, 0, 0));
  }

  get orientation() {
    // check type in case it was serialized when passed to worker
    if (!(this.orient instanceof Vector3)) {
      this.orient = new Vector3(this.planeNormal.X, this.planeNormal.Y, this.planeNormal.Z);
    }

    return this.orient;
  }

  set upDir(up) {
    this.up = new Vector3(up.X, up.Y, up.Z);
  }

  get upDir() {
    // check type in case it was serialized when passed to worker
    if (!(this.up instanceof Vector3)) {
      this.up = new Vector3(this.up.x, this.up.y, this.up.z);
    }

    return this.up;
  }

  get xDir() {
    // check type in case it was serialized when passed to worker
    if (!(this.x instanceof Vector3)) {
      this.x = new Vector3().crossVectors(this.orientation, this.upDir);
    }

    return this.x;
  }

  get sliceThickness() {
    return Math.abs(this.volume3D.size.dot(this.orientation) / this.numSlices);
  }

  get size() {
    // check type in case it was serialized when passed to worker
    if (!(this.sliceSize instanceof Vector2)) {
      const x = Math.abs(this.xDir.dot(this.volume3D.size));
      const y = Math.abs(this.upDir.dot(this.volume3D.size));
      this.sliceSize = new Vector2(x, y);
    }

    return this.sliceSize;
  }

  get cameraPosition() {
    // check type in case it was serialized when passed to worker
    if (!(this.camPosition instanceof Vector3)) {
      this.camPosition = this.orientation
        .clone()
        .multiply(this.volume3D.size)
        .negate()
        .add(this.volume3D.center);
    }

    return this.camPosition;
  }

  calcSliceIdx(position) {
    const min = this.volume3D.boundingBox.min.dot(this.orientation) + 0.5 * this.sliceThickness;
    const max = this.volume3D.boundingBox.max.dot(this.orientation) - 0.5 * this.sliceThickness;
    const minPt = Math.min(min, max);
    const maxPt = Math.max(min, max);

    const boundingSize = maxPt - minPt;
    const sliceLocation = position.dot(this.orientation);

    return Math.round(((sliceLocation - minPt) / boundingSize) * (this.numSlices - 1));
  }

  // distance from start of first slice
  calcSlicePosition(sliceIdx) {
    const slicePos = sliceIdx / (this.numSlices - 1);
    const sliceViewSize = Math.abs(this.volume3D.size.dot(this.orientation)) - this.sliceThickness;
    return slicePos * sliceViewSize;
  }

  getSlicePlane(sliceIdx) {
    tempVec
      .copy(this.orientation)
      .multiplyScalar(this.calcSlicePosition(sliceIdx))
      .add(this.volume3D.boundingBox.min)
      .add(this.orientation.clone().multiplyScalar(0.5 * this.sliceThickness));

    return new Plane().setFromNormalAndCoplanarPoint(this.orientation, tempVec);
  }
}
